import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import css from './LoginsTable.css';
import { Table, TableCell } from '../../../components/widgets';
import TableCellButton from '../../../components/widgets/TableCellButton';
import { getColumnValue, getLoginsTableConfig } from './tableConfig';

const REAUTHORIZE_FIELD = 'reauthorize';
export const ACCOUNTS_TABLE_DATA_TEST_ID = 'user-configuration-accounts-table';

const createCellWithGetter = getValue => props => <TableCell {...props} value={getValue(props.row)} />;

export default function LoginsTable({
    accounts,
    searchText,
    partnerName,
    partnerDisplayName,
    icon,
    triggerOAuthFlow,
    onDoneReady,
    onClose,
    onDone,
}) {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const tableConfig = useMemo(() => getLoginsTableConfig(partnerName), [partnerName]);

    const filteredLogins = useMemo(() => {
        if (!searchText) return accounts;
        const lowerSearch = searchText.toLowerCase();
        return accounts.filter(account =>
            tableConfig.columns.some(col =>
                getColumnValue(account, col)
                    ?.toLowerCase()
                    .includes(lowerSearch)
            )
        );
    }, [accounts, searchText, tableConfig.columns]);

    const sortedLogins = useMemo(() => {
        if (sortDirection === null || sortColumn === null) return filteredLogins;

        const sortCol = tableConfig.columns.find(col => col.name === sortColumn);
        if (!sortCol) return filteredLogins;

        return [...filteredLogins].sort((a, b) => {
            const aVal = String(getColumnValue(a, sortCol) || '').toLowerCase();
            const bVal = String(getColumnValue(b, sortCol) || '').toLowerCase();
            if (sortDirection === 'asc') return aVal.localeCompare(bVal);
            return bVal.localeCompare(aVal);
        });
    }, [filteredLogins, sortDirection, sortColumn, tableConfig.columns]);

    const tableMetadata = useMemo(() => {
        const handleColumnSort = columnName => {
            if (sortColumn === columnName) {
                setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortColumn(columnName);
                setSortDirection('asc');
            }
        };

        const columnMetadata = tableConfig.columns.map(col => ({
            name: col.name,
            displayName: col.displayName,
            cellComponent: col.getValue ? createCellWithGetter(col.getValue) : TableCell,
            headerAlign: 'left',
            headerWidth: col.headerWidth,
            boldHeader: true,
            showHeaderArrow: true,
            headerArrow: sortColumn === col.name ? (sortDirection === 'asc' ? 'up' : 'down') : null,
            headerClick: () => handleColumnSort(col.name),
        }));

        columnMetadata.push({
            name: REAUTHORIZE_FIELD,
            displayName: '',
            cellComponent: TableCellButton,
            headerAlign: 'right',
            cellProps: {
                translationId: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.REAUTHORIZE',
                type: 'secondary',
                onClick: triggerOAuthFlow,
                tdClassName: css.reauthorizeCell,
            },
        });

        return columnMetadata;
    }, [tableConfig, sortColumn, sortDirection, triggerOAuthFlow]);

    const handleDone = useCallback(() => {
        (onDone || onClose)();
    }, [onDone, onClose]);

    const isDoneDisabled = onDone && accounts.length === 0;

    useEffect(() => {
        onDoneReady({ handleDone, disabled: isDoneDisabled, loading: false });
    }, [handleDone, isDoneDisabled, onDoneReady]);

    if (filteredLogins.length === 0) {
        return (
            <div className={css.emptyState}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_MATCHING_ACCOUNTS" />
            </div>
        );
    }

    return (
        <div className={css.loginsContainer}>
            <div className={css.loginsHeader}>
                <img src={icon} alt={partnerDisplayName} className={css.partnerLogo} />
                <span className={css.loginsTitle}>
                    <Translate
                        id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ACCOUNTS_HEADER"
                        data={{ partnerName: partnerDisplayName, count: accounts.length }}
                    />
                </span>
            </div>
            <Table
                dataTestId={ACCOUNTS_TABLE_DATA_TEST_ID}
                data={sortedLogins}
                metadata={tableMetadata}
                trClass={css.tableRow}
                theadClass={classNames(css.tableHeader, {
                    [css.showHeaderSeparators]: tableConfig.showHeaderSeparators,
                })}
                zebraTable={false}
                rowHoverEnabled={false}
                showInnerBorder
            />
        </div>
    );
}

LoginsTable.propTypes = {
    accounts: PropTypes.array.isRequired,
    searchText: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
    partnerDisplayName: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    triggerOAuthFlow: PropTypes.func.isRequired,
    onDoneReady: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onDone: PropTypes.func,
};
